import { Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, KeyRound } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { DetailFact, DetailGrid, DetailSection, MetadataTable, formatDateTime, truncateMiddle } from "@/components/common/AdminDetails";
import { ErrorState, LoadingRow } from "@/components/common/StateViews";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { tenantApi, type Tenant } from "@/lib/proxy-api";
import { ApiException } from "@/lib/api-client";
import { canManageTenant, useTenantWorkspace } from "@/lib/tenant-workspace";

export function TenantWorkspacePage() {
  const qc = useQueryClient();
  const workspace = useTenantWorkspace();
  const [adminKey, setAdminKey] = useState("");
  const [readerKey, setReaderKey] = useState("");
  const [writerKey, setWriterKey] = useState("");
  const [showKeyForm, setShowKeyForm] = useState(false);
  const [showReaderKeyForm, setShowReaderKeyForm] = useState(false);
  const [showWriterKeyForm, setShowWriterKeyForm] = useState(false);

  if (!workspace.tenantId) return <Navigate to="/tenants" replace />;

  const { data, isLoading, error } = useQuery({
    queryKey: ["tenant", workspace.tenantId],
    queryFn: () => tenantApi.get(workspace.tenantId!),
  });

  const tenant = data as Tenant | undefined;
  const canManage = canManageTenant(workspace.role);
  const tenantKeyKnown = tenant?.hasTenantAdminKey;
  const readerKeyKnown = tenant?.hasTenantReaderKey;
  const writerKeyKnown = tenant?.hasTenantWriterKey;
  const keyRequired = isTenantKeyRequired(error);
  const storeKeyMut = useMutation({
    mutationFn: () => tenantApi.storeAdminKey(workspace.tenantId!, adminKey),
    onSuccess: () => {
      setAdminKey("");
      setShowKeyForm(false);
      qc.invalidateQueries({ queryKey: ["tenant", workspace.tenantId] });
      qc.invalidateQueries({ queryKey: ["tenants"] });
    },
  });
  const storeReaderKeyMut = useMutation({
    mutationFn: () => tenantApi.storeRoleKey(workspace.tenantId!, "reader", readerKey),
    onSuccess: () => {
      setReaderKey("");
      setShowReaderKeyForm(false);
      qc.invalidateQueries({ queryKey: ["tenant", workspace.tenantId] });
      qc.invalidateQueries({ queryKey: ["tenants"] });
    },
  });
  const storeWriterKeyMut = useMutation({
    mutationFn: () => tenantApi.storeRoleKey(workspace.tenantId!, "writer", writerKey),
    onSuccess: () => {
      setWriterKey("");
      setShowWriterKeyForm(false);
      qc.invalidateQueries({ queryKey: ["tenant", workspace.tenantId] });
      qc.invalidateQueries({ queryKey: ["tenants"] });
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title={tenant?.name ?? workspace.tenantName ?? "Tenant Workspace"}
        description="Tenant-owned collections, documents, API keys, and knowledge base operations."
      />

      {isLoading ? (
        <LoadingRow cols={3} />
      ) : error ? (
        keyRequired ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            This tenant workspace needs a stored Tenant Admin API key before ServisSearch tenant details can be loaded.
          </div>
        ) : (
          <ErrorState message={error instanceof ApiException ? error.message : "Failed to load tenant"} />
        )
      ) : tenant ? (
        <div className="space-y-6 rounded-lg border bg-card p-4">
          <DetailSection title="Tenant identity">
            <DetailGrid>
              <DetailFact label="Tenant ID" value={tenant.id} />
              <DetailFact label="Identifier" value={tenant.identifier} />
              <DetailFact label="Status" value={<StatusBadge status={tenant.status} />} />
              <DetailFact label="Tenant Admin Key" value={<KeyStatusBadge hasKey={tenant.hasTenantAdminKey} />} />
              <DetailFact label="Tenant Reader Key" value={<KeyStatusBadge hasKey={tenant.hasTenantReaderKey} />} />
              <DetailFact label="Tenant Writer Key" value={<KeyStatusBadge hasKey={tenant.hasTenantWriterKey} />} />
              <DetailFact label="Created" value={formatDateTime(tenant.created_at)} />
              <DetailFact label="Updated" value={formatDateTime(tenant.updated_at)} />
            </DetailGrid>
          </DetailSection>

          <DetailSection title="Knowledge base and storage">
            <DetailGrid>
              <DetailFact label="Knowledge Base" value={tenant.knowledge_base_id} />
              <DetailFact label="Knowledge Base Status" value={tenant.knowledge_base_status} />
              <DetailFact label="Data Source" value={tenant.data_source_id} />
              <DetailFact label="Documents Bucket" value={tenant.documents_bucket} />
              <DetailFact label="Vector Bucket" value={tenant.vector_bucket_name} />
              <DetailFact label="Vector Index" value={tenant.vector_index_name} />
              <DetailFact label="Vector Index ARN" value={truncateMiddle(tenant.vector_index_arn, 24)} className="lg:col-span-2" />
            </DetailGrid>
          </DetailSection>

          <DetailSection title="LLM and embedding defaults">
            <DetailGrid>
              <DetailFact label="Effective Provider" value={tenant.effective_defaults?.llm_provider_type ?? tenant.llm_provider?.type} />
              <DetailFact label="Effective Model" value={tenant.effective_defaults?.llm_model ?? tenant.llm_provider?.default_model} />
              <DetailFact label="Temperature" value={tenant.effective_defaults?.llm_temperature} />
              <DetailFact label="Max Tokens" value={tenant.effective_defaults?.llm_max_tokens} />
              <DetailFact label="Embedding Model" value={tenant.effective_defaults?.embedding_model ?? truncateMiddle(tenant.embedding_model_arn, 24)} className="lg:col-span-2" />
              <DetailFact label="Provider Base URL" value={tenant.llm_provider?.openai_config?.base_url} />
              <DetailFact label="Provider Key" value={tenant.llm_provider?.openai_config?.api_key ? "stored (masked)" : "not exposed"} />
              <DetailFact label="Provider Default Model" value={tenant.llm_provider?.openai_config?.default_model ?? tenant.llm_provider?.default_model} />
              <DetailFact label="Provider Organization" value={tenant.llm_provider?.openai_config?.organization} />
            </DetailGrid>
          </DetailSection>

          {canManage && (
            <TenantLlmEditor
              tenant={tenant}
              onSave={(llmProvider) => tenantApi.update(workspace.tenantId!, { llm_provider: llmProvider })}
              onReset={() => tenantApi.update(workspace.tenantId!, { llm_provider: null })}
              onSaved={() => {
                qc.invalidateQueries({ queryKey: ["tenant", workspace.tenantId] });
                qc.invalidateQueries({ queryKey: ["tenants"] });
                qc.invalidateQueries({ queryKey: ["collections", workspace.tenantId] });
              }}
            />
          )}

          <DetailSection title="Custom metadata">
            <MetadataTable value={tenant.custom_metadata} emptyLabel="No custom metadata exposed for this tenant." />
          </DetailSection>

          {tenant.description && (
            <p className="mt-4 border-t pt-4 text-sm text-muted-foreground">{tenant.description}</p>
          )}
        </div>
      ) : (
        <ErrorState message="Tenant not found" />
      )}

      {canManage && (
        <section className="space-y-4 rounded-lg border bg-card p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-md border bg-background">
                <KeyRound className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <h2 className="text-sm font-semibold">Tenant Admin API key</h2>
                <div className="mt-1">
                  <KeyStatusBadge hasKey={tenantKeyKnown} />
                </div>
              </div>
            </div>
            <Button
              type="button"
              variant={tenantKeyKnown ? "outline" : "default"}
              onClick={() => setShowKeyForm((value) => !value)}
            >
              <KeyRound className="h-4 w-4" />
              {tenantKeyKnown ? "Rotate key" : "Store key"}
            </Button>
          </div>

          {showKeyForm && (
            <form
              className="mt-4 space-y-3 border-t pt-4"
              onSubmit={(e) => {
                e.preventDefault();
                storeKeyMut.mutate();
              }}
            >
              <div className="space-y-1.5">
                <Label htmlFor="tenant-admin-key">New Tenant Admin API key</Label>
                <Input
                  id="tenant-admin-key"
                  type="password"
                  value={adminKey}
                  onChange={(e) => setAdminKey(e.target.value)}
                  placeholder={tenantKeyKnown ? "Paste replacement key" : "Paste Tenant Admin key"}
                  autoComplete="off"
                  autoFocus
                />
              </div>
              {storeKeyMut.isError && (
                <p className="text-sm text-destructive">
                  {storeKeyMut.error instanceof ApiException ? storeKeyMut.error.message : "Failed to store key"}
                </p>
              )}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setAdminKey(""); setShowKeyForm(false); }}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!adminKey || storeKeyMut.isPending}>
                  {storeKeyMut.isPending ? "Saving…" : tenantKeyKnown ? "Save replacement" : "Save key"}
                </Button>
              </div>
            </form>
          )}

          <TenantRoleKeyForm
            title="Tenant Reader API key"
            description="Used server-side by Search and RAG playgrounds. The key is never sent to the browser."
            known={readerKeyKnown}
            value={readerKey}
            onValueChange={setReaderKey}
            open={showReaderKeyForm}
            onOpenChange={setShowReaderKeyForm}
            pending={storeReaderKeyMut.isPending}
            error={storeReaderKeyMut.error}
            onSubmit={() => storeReaderKeyMut.mutate()}
          />

          <TenantRoleKeyForm
            title="Tenant Writer API key"
            description="Used server-side for document upload, delete, and sync operations. The key is never sent to the browser."
            known={writerKeyKnown}
            value={writerKey}
            onValueChange={setWriterKey}
            open={showWriterKeyForm}
            onOpenChange={setShowWriterKeyForm}
            pending={storeWriterKeyMut.isPending}
            error={storeWriterKeyMut.error}
            onSubmit={() => storeWriterKeyMut.mutate()}
          />
        </section>
      )}
    </div>
  );
}

function TenantRoleKeyForm({
  title,
  description,
  known,
  value,
  onValueChange,
  open,
  onOpenChange,
  pending,
  error,
  onSubmit,
}: {
  title: string;
  description: string;
  known?: boolean;
  value: string;
  onValueChange: (value: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pending: boolean;
  error: unknown;
  onSubmit: () => void;
}) {
  return (
    <div className="border-t pt-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">{title}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          <div className="mt-2">
            <KeyStatusBadge hasKey={known} />
          </div>
        </div>
        <Button type="button" variant={known ? "outline" : "default"} onClick={() => onOpenChange(!open)}>
          <KeyRound className="h-4 w-4" />
          {known ? "Rotate key" : "Store key"}
        </Button>
      </div>

      {open && (
        <form
          className="mt-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <div className="space-y-1.5">
            <Label>{known ? "Replacement key" : "API key"}</Label>
            <Input
              type="password"
              value={value}
              onChange={(e) => onValueChange(e.target.value)}
              placeholder={known ? "Paste replacement key" : "Paste API key"}
              autoComplete="off"
            />
          </div>
          {Boolean(error) && (
            <p className="text-sm text-destructive">
              {error instanceof ApiException ? error.message : "Failed to store key"}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => { onValueChange(""); onOpenChange(false); }}>
              Cancel
            </Button>
            <Button type="submit" disabled={!value || pending}>
              {pending ? "Saving…" : known ? "Save replacement" : "Save key"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status?: string }) {
  if (!status) return <Badge variant="outline">unknown</Badge>;
  const tone =
    status === "active"
      ? "bg-green-50 text-green-700 border-green-200"
      : status === "provisioning"
        ? "bg-amber-50 text-amber-700 border-amber-200"
        : "bg-red-50 text-red-700 border-red-200";
  return <Badge variant="outline" className={tone}>{status}</Badge>;
}

function KeyStatusBadge({ hasKey }: { hasKey?: boolean }) {
  return hasKey ? (
    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
      <CheckCircle2 className="mr-1 h-3 w-3" />
      stored
    </Badge>
  ) : (
    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
      <AlertTriangle className="mr-1 h-3 w-3" />
      missing
    </Badge>
  );
}

function TenantLlmEditor({
  tenant,
  onSave,
  onReset,
  onSaved,
}: {
  tenant: Tenant;
  onSave: (llmProvider: NonNullable<Tenant["llm_provider"]>) => Promise<unknown>;
  onReset: () => Promise<unknown>;
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [providerType, setProviderType] = useState(tenant.llm_provider?.type ?? "openai-compatible");
  const [baseUrl, setBaseUrl] = useState(tenant.llm_provider?.openai_config?.base_url ?? "");
  const [apiKey, setApiKey] = useState("");
  const [model, setModel] = useState(
    tenant.llm_provider?.openai_config?.default_model ??
    tenant.llm_provider?.default_model ??
    tenant.effective_defaults?.llm_model ??
    "",
  );
  const [temperature, setTemperature] = useState(String(tenant.llm_provider?.default_temperature ?? tenant.effective_defaults?.llm_temperature ?? 0));
  const [maxTokens, setMaxTokens] = useState(String(tenant.llm_provider?.default_max_tokens ?? tenant.effective_defaults?.llm_max_tokens ?? ""));
  const [organization, setOrganization] = useState(tenant.llm_provider?.openai_config?.organization ?? "");
  const [extraParams, setExtraParams] = useState(formatJson(tenant.llm_provider?.extra_params));
  const [openaiExtraParams, setOpenaiExtraParams] = useState(formatJson(tenant.llm_provider?.openai_config?.extra_params));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setProviderType(tenant.llm_provider?.type ?? "openai-compatible");
    setBaseUrl(tenant.llm_provider?.openai_config?.base_url ?? "");
    setApiKey("");
    setModel(tenant.llm_provider?.openai_config?.default_model ?? tenant.llm_provider?.default_model ?? tenant.effective_defaults?.llm_model ?? "");
    setTemperature(String(tenant.llm_provider?.default_temperature ?? tenant.effective_defaults?.llm_temperature ?? 0));
    setMaxTokens(String(tenant.llm_provider?.default_max_tokens ?? tenant.effective_defaults?.llm_max_tokens ?? ""));
    setOrganization(tenant.llm_provider?.openai_config?.organization ?? "");
    setExtraParams(formatJson(tenant.llm_provider?.extra_params));
    setOpenaiExtraParams(formatJson(tenant.llm_provider?.openai_config?.extra_params));
  }, [tenant]);

  const saveMut = useMutation({
    mutationFn: async () => {
      setError(null);
      const parsedExtra = parseOptionalJson(extraParams, "Provider extra params");
      const parsedOpenaiExtra = parseOptionalJson(openaiExtraParams, "OpenAI-compatible extra params");
      if (providerType === "openai-compatible") {
        if (!baseUrl.trim()) throw new Error("Base URL is required for openai-compatible providers");
        if (!model.trim()) throw new Error("Default model is required");
        if (!apiKey.trim()) {
          throw new Error("Paste the LLM API key when saving. ServisSearch masks the existing key, so this UI cannot preserve it during provider updates.");
        }
      }
      return onSave({
        type: providerType,
        openai_config: providerType === "openai-compatible"
          ? {
              base_url: baseUrl.trim(),
              api_key: apiKey.trim(),
              default_model: model.trim(),
              ...(organization.trim() ? { organization: organization.trim() } : {}),
              ...(parsedOpenaiExtra ? { extra_params: parsedOpenaiExtra } : {}),
            }
          : undefined,
        default_model: providerType === "openai-compatible" ? undefined : model.trim() || undefined,
        default_temperature: numberOrUndefined(temperature),
        default_max_tokens: numberOrUndefined(maxTokens),
        ...(parsedExtra ? { extra_params: parsedExtra } : {}),
      });
    },
    onSuccess: () => {
      setApiKey("");
      setOpen(false);
      onSaved();
    },
    onError: (err) => setError(err instanceof Error ? err.message : "Failed to save LLM provider"),
  });

  const resetMut = useMutation({
    mutationFn: onReset,
    onSuccess: () => {
      setOpen(false);
      onSaved();
    },
    onError: (err) => setError(err instanceof Error ? err.message : "Failed to reset LLM provider"),
  });

  return (
    <section className="rounded-lg border p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Tenant LLM defaults</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Collections currently inherit this tenant-level RAG model configuration.
          </p>
        </div>
        <Button type="button" variant="outline" onClick={() => setOpen((value) => !value)}>
          {open ? "Close editor" : "Edit LLM defaults"}
        </Button>
      </div>

      {open && (
        <form
          className="mt-4 space-y-4 border-t pt-4"
          onSubmit={(e) => {
            e.preventDefault();
            saveMut.mutate();
          }}
        >
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Provider type</Label>
              <select className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm" value={providerType} onChange={(e) => setProviderType(e.target.value)}>
                <option value="openai-compatible">openai-compatible</option>
                <option value="bedrock">bedrock</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Default model</Label>
              <Input value={model} onChange={(e) => setModel(e.target.value)} placeholder="nvidia.nemotron-super-3-120b" />
            </div>
            {providerType === "openai-compatible" && (
              <>
                <div className="space-y-1.5 md:col-span-2">
                  <Label>Base URL</Label>
                  <Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://bedrock-mantle.eu-west-1.api.aws/v1/chat/completions" />
                </div>
                <div className="space-y-1.5">
                  <Label>LLM API key</Label>
                  <Input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="Paste key for this save" autoComplete="off" />
                </div>
                <div className="space-y-1.5">
                  <Label>Organization</Label>
                  <Input value={organization} onChange={(e) => setOrganization(e.target.value)} placeholder="optional" />
                </div>
              </>
            )}
            <div className="space-y-1.5">
              <Label>Temperature</Label>
              <Input type="number" step="0.01" value={temperature} onChange={(e) => setTemperature(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Max tokens</Label>
              <Input type="number" min={1} value={maxTokens} onChange={(e) => setMaxTokens(e.target.value)} placeholder="optional" />
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Provider extra params JSON</Label>
              <textarea className="min-h-28 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm font-mono" value={extraParams} onChange={(e) => setExtraParams(e.target.value)} placeholder="{ }" />
            </div>
            <div className="space-y-1.5">
              <Label>OpenAI-compatible extra params JSON</Label>
              <textarea className="min-h-28 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm font-mono" value={openaiExtraParams} onChange={(e) => setOpenaiExtraParams(e.target.value)} placeholder="{ }" />
            </div>
          </div>

          <div className="rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
            ServisSearch masks the existing LLM key in reads. Until the API supports key-only rotation or merge-patch semantics, saving an openai-compatible provider requires pasting the key.
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-between gap-2">
            <Button type="button" variant="outline" disabled={resetMut.isPending} onClick={() => resetMut.mutate()}>
              Reset to system default
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saveMut.isPending}>{saveMut.isPending ? "Saving…" : "Save LLM defaults"}</Button>
            </div>
          </div>
        </form>
      )}
    </section>
  );
}

function parseOptionalJson(value: string, label: string): Record<string, unknown> | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = JSON.parse(trimmed) as unknown;
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    throw new Error(`${label} must be a JSON object`);
  }
  return parsed as Record<string, unknown>;
}

function formatJson(value: Record<string, unknown> | undefined | null): string {
  return value ? JSON.stringify(value, null, 2) : "";
}

function numberOrUndefined(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const number = Number(trimmed);
  return Number.isFinite(number) ? number : undefined;
}

function isTenantKeyRequired(error: unknown): boolean {
  return error instanceof ApiException && error.code === "TENANT_KEY_REQUIRED";
}
